// WebSocket服务 - 管理WebSocket连接和消息
import { isDiagEnabled } from '../debug';

// trace_id 环形缓冲。以前挂在 window.__laneChatLastTraceId / __laneChatTraceHistory 上，
// 等于在每个客户页面无条件暴露我们的内部链路 id —— 现在收进模块作用域，
// 只有诊断模式打开时才往 window 上挂只读快照。
const TRACE_HISTORY_MAX = 20;
let lastTraceId = null;
const traceHistory = [];

class WebSocketService {
    constructor() {
        this.socket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 5000;
        this.heartbeatInterval = null;
        this.messageHandlers = new Map();
        this.connectionHandlers = new Set();
        this.isConnecting = false;
        this.isReconnecting = false;
        this.messageQueue = [];

        // WebSocket服务器配置 - 从配置中获取
        this.wsUrl = null; // 将在connect时从配置获取

        // 保存连接参数用于重连
        this.clientId = null;
        this.appId = null;

        // 自动重连
        this.autoReconnect = true;
    }

    // 连接WebSocket
    async connect(clientId = null, appId = null) {
        // 保存连接参数用于重连
        if (clientId !== null) this.clientId = clientId;
        if (appId !== null) this.appId = appId;

        if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
            console.log('WebSocket already connected or connecting');
            return Promise.resolve();
        }

        this.isConnecting = true;

        return new Promise(async (resolve, reject) => {
            try {
                // 从配置获取WebSocket URL——REST 与 WS 共用同一份"当前生效域名"
                // 判定（server-config.js 唯一真源），不会出现 REST 走 A、WS 走 B。
                const { getWsUrl, isCurrentApiHostPrimary, reportApiConnectionFailure, PRIMARY_PROBE_TIMEOUT_MS } =
                    await import('../config/server-config.js');
                const wsUrl = getWsUrl();
                const onPrimary = isCurrentApiHostPrimary();

                // 构建连接URL参数
                const params = new URLSearchParams();
                if (this.appId) params.append('appId', this.appId);
                // 优先使用 JWT token 认证
                const { default: ApiServiceInstance } = await import('./api.js');
                const jwt = ApiServiceInstance.getToken();
                if (jwt) {
                    params.append('token', jwt);
                } else if (this.clientId) {
                    params.append('userId', this.clientId);
                }

                const url = params.toString() ? `${wsUrl}?${params}` : wsUrl;
                console.log('Connecting to WebSocket:', url);

                this.socket = new WebSocket(url);

                // 本次连接尝试是否已经成功握手过——onerror/onclose 用它判断这是不是
                // "从未连上过"的连接层失败（握手失败），而不是"连上过、后来正常断开"。
                let openedThisAttempt = false;

                // 主域名判死用短探测超时（被墙用户不该陪 WS 陪到操作系统级 TCP 超时，
                // 那可能是几十秒）；命中且尚未握手成功 → 当连接层失败处理，切 fallback
                // 重连一次。已经是 fallback、或本来就走显式覆盖/本地开发时不设这个表。
                let primaryProbeTimer = null;
                if (onPrimary) {
                    primaryProbeTimer = setTimeout(() => {
                        if (openedThisAttempt) return;
                        console.warn('WebSocket primary host probe timed out, failing over');
                        reportApiConnectionFailure();
                        try { this.socket && this.socket.close(); } catch (e) { /* ignore */ }
                        this.isConnecting = false;
                        // 立即用新域名重连一次，不等指数退避——这就是这次 connect() 的结果。
                        this.connect().then(resolve, reject);
                    }, PRIMARY_PROBE_TIMEOUT_MS);
                }

                this.socket.onopen = () => {
                    openedThisAttempt = true;
                    if (primaryProbeTimer) clearTimeout(primaryProbeTimer);
                    console.log('WebSocket connected');
                    this.isConnecting = false;
                    this.isReconnecting = false;
                    this.reconnectAttempts = 0;

                    // 开始心跳
                    this.startHeartbeat();

                    // 处理排队的消息
                    this.processQueuedMessages();

                    // 通知连接处理器
                    this.notifyConnectionHandlers('connected');

                    resolve();
                };

                this.socket.onmessage = (event) => {
                    this.handleMessage(event);
                };

                this.socket.onclose = (event) => {
                    if (primaryProbeTimer) clearTimeout(primaryProbeTimer);
                    console.log('WebSocket disconnected', event.code, event.reason);
                    this.isConnecting = false;
                    this.stopHeartbeat();

                    // 从未握手成功就关闭 = 握手失败（连接层失败）。主域名下第一时间
                    // 切 fallback，避免后续重连继续吃同一个打不通的域名。
                    if (!openedThisAttempt && onPrimary) {
                        reportApiConnectionFailure();
                    }

                    // 通知连接处理器
                    this.notifyConnectionHandlers('disconnected');

                    // 自动重连
                    if (this.autoReconnect && !this.isReconnecting) {
                        this.reconnect();
                    }
                };

                this.socket.onerror = (error) => {
                    if (primaryProbeTimer) clearTimeout(primaryProbeTimer);
                    console.error('WebSocket error:', error);
                    this.isConnecting = false;

                    // 握手阶段的 onerror（从未连上过）也当连接层失败处理；已经握手成功
                    // 过、运行期报错的场景不切域名（那不代表主域名不可达）。
                    if (!openedThisAttempt && onPrimary) {
                        reportApiConnectionFailure();
                    }

                    reject(error);
                };

            } catch (error) {
                this.isConnecting = false;
                reject(error);
            }
        });
    }

    // 断开连接
    disconnect() {
        this.autoReconnect = false;
        this.stopHeartbeat();

        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }

        // 只清空消息队列，保留handlers以便重连后继续工作
        this.messageQueue = [];
    }

    // 重连
    reconnect() {
        if (this.isReconnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('Max reconnection attempts reached or already reconnecting');
            return;
        }

        this.isReconnecting = true;
        this.reconnectAttempts++;

        console.log(`Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

        setTimeout(() => {
            this.connect().catch(error => {
                console.error('Reconnection failed:', error);
                this.isReconnecting = false;

                if (this.autoReconnect) {
                    this.reconnect();
                }
            });
        }, delay);
    }

    // 发送消息
    send(data) {
        const message = typeof data === 'string' ? data : JSON.stringify(data);

        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(message);
            return true;
        } else {
            console.log('WebSocket not ready, queueing message');
            this.messageQueue.push(message);

            // 尝试重连
            if (!this.isConnecting && !this.isReconnecting) {
                this.connect();
            }

            return false;
        }
    }

    // 处理消息
    handleMessage(event) {
        try {
            const data = JSON.parse(event.data);

            // 心跳响应
            if (data.type === 'pong') {
                return;
            }

            // TraceId debug helper: capture trace_id from metadata（只进模块作用域）
            if (data.metadata && data.metadata.trace_id) {
                lastTraceId = data.metadata.trace_id;
                traceHistory.unshift({ trace_id: data.metadata.trace_id, ts: new Date().toISOString() });
                if (traceHistory.length > TRACE_HISTORY_MAX) traceHistory.length = TRACE_HISTORY_MAX;
            }

            // 根据消息类型分发
            const handlers = this.messageHandlers.get(data.type);
            if (handlers) {
                handlers.forEach(handler => {
                    try {
                        handler(data);
                    } catch (err) {
                        console.error('Error in message handler:', err);
                    }
                });
            }

            // 通用消息处理器
            const generalHandlers = this.messageHandlers.get('*');
            if (generalHandlers) {
                generalHandlers.forEach(handler => {
                    try {
                        handler(data);
                    } catch (err) {
                        console.error('Error in general handler:', err);
                    }
                });
            }

        } catch (error) {
            console.error('Error handling message:', error, event.data);
        }
    }

    // 注册消息处理器
    on(type, handler) {
        if (!this.messageHandlers.has(type)) {
            this.messageHandlers.set(type, new Set());
        }
        this.messageHandlers.get(type).add(handler);
    }

    // 移除消息处理器
    off(type, handler) {
        const handlers = this.messageHandlers.get(type);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.messageHandlers.delete(type);
            }
        }
    }

    // 注册连接状态处理器
    onConnectionChange(handler) {
        this.connectionHandlers.add(handler);
    }

    // 移除连接状态处理器
    offConnectionChange(handler) {
        this.connectionHandlers.delete(handler);
    }

    // 通知连接处理器
    notifyConnectionHandlers(status) {
        this.connectionHandlers.forEach(handler => {
            try {
                handler(status);
            } catch (error) {
                console.error('Error in connection handler:', error);
            }
        });
    }

    // 处理排队的消息
    processQueuedMessages() {
        console.log(`Processing ${this.messageQueue.length} queued messages`);

        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            this.send(message);
        }
    }

    // 开始心跳
    startHeartbeat() {
        this.stopHeartbeat();

        this.heartbeatInterval = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000); // 每30秒发送一次
    }

    // 停止心跳
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    // 获取连接状态
    get readyState() {
        return this.socket ? this.socket.readyState : WebSocket.CLOSED;
    }

    // 是否已连接
    get isConnected() {
        return this.socket && this.socket.readyState === WebSocket.OPEN;
    }

    // 清理队列中的消息
    clearQueue() {
        this.messageQueue = [];
    }

    // 设置最大重连次数
    setMaxReconnectAttempts(attempts) {
        this.maxReconnectAttempts = attempts;
    }

    // 设置重连延迟
    setReconnectDelay(delay) {
        this.reconnectDelay = delay;
    }

    // 手动触发重连
    forceReconnect() {
        this.autoReconnect = true;
        this.reconnectAttempts = 0;

        if (this.socket) {
            this.socket.close();
        }

        return this.connect();
    }
}

// 导出单例
export default new WebSocketService();

// 内部排障用的 trace 查看器。只在诊断模式下挂出来，客户页面上不存在这个全局。
// 判定发生在模块加载时，所以要先 localStorage.setItem('__lcx', ...) 再刷新页面。
if (typeof window !== 'undefined' && isDiagEnabled()) {
    window.__lcxTrace = {
        lastTraceId: () => lastTraceId,
        history: () => traceHistory.slice(),
    };
}